
import { Component, Vue } from 'vue-property-decorator';
import dayjs from 'dayjs';
import Avatar from '@/components/Avatar.vue';
import PageTitle from '@/components/PageTitle.vue';
import * as UserServices from '@/services/userSpace/UserService';
import ConfirmProfileUpdate from '@/components/Dialogs/ConfirmProfileUpdate.vue';
import { UserInfosForPatch, UpdateProfileFormRules } from '@/services/userSpace/types';
import { getUIConfig } from '@/configs';
import { PlatformKey } from '@/constants';
import { getTranslation } from '@/i18n';

const moment = require('moment');

moment.locale('fr');

@Component({
  computed: {
    PlatformKey() {
      return PlatformKey;
    },
  },
  components: {
    ConfirmProfileUpdate,
    Avatar,
    PageTitle,
  },
})
export default class UpdateProfile extends Vue {
  formValid: boolean = false;

  showConfirm = false;

  errorDateOfBirth = '';

  errorEmail = '';

  errorContactingService = '';

  mustReconnect: boolean = false;

  emailResent: boolean = false;

  emailResentLoading: boolean = false;

  emailResentError: string = '';

  snackbar: boolean = false;

  hasConfirmedEmail: boolean = false;

  userId: number = -1;

  form: UserInfosForPatch = {
    email: '',
    lastname: '',
    firstname: '',
    phoneNumber: '',
    deliveryAdress: '',
    zipCode: null,
    city: '',
    newsletter: false,
  };

  initialForm: UserInfosForPatch = this.form;

  formRules: UpdateProfileFormRules = UserServices.formRules;

  birthDateInvalid: boolean = false;

  validIsDisabled: boolean = true;

  emailAsExternal = false;

  disableFieldForPlatform = [PlatformKey.TBM];

  async mounted() {
    this.emailResentError = '';
    await this.loadForm();

    this.$nextTick(() => {
      (this.$refs.updateProfileForm as Vue & { validate: () => boolean }).validate();
      this.birthDateInvalid =
        (
          this.$refs.dateOfBirth as Vue & {
            errorBucket: [];
          }
        ).errorBucket.length > 0;
    });
  }

  async loadForm() {
    const { userInfosForPatch, additionalInfos } = await UserServices.getUserProfile();

    this.userId = additionalInfos.id;
    this.hasConfirmedEmail = additionalInfos.emailConfirmation;

    this.initialForm = {
      ...userInfosForPatch,
      dateOfBirth: userInfosForPatch.dateOfBirth ? dayjs(userInfosForPatch.dateOfBirth).format('DD/MM/YYYY') : '',
    };
    this.form = { ...this.initialForm };
  }

  get platform() {
    return this.$store.getters['platform/getPlatform'];
  }

  get userNameAbbreviation() {
    const firstNameInitial = this.form.firstname ? this.form.firstname[0] : '';
    const lastNameInitial = this.form.lastname ? this.form.lastname[0] : '';
    return `${firstNameInitial}${lastNameInitial}`;
  }

  get columnCount() {
    return this.$vuetify.breakpoint.smAndUp ? 10 : 5;
  }

  // TODO: create and use a plugin instead
  get showNewsletterSwitch() {
    return getUIConfig(this.platform.key, 'profil.showNewsletterSwitch');
  }

  get hasExternalLinkUpdate() {
    return getUIConfig(this.platform.key, 'profil.externalUpdateUrl');
  }

  get updateLink() {
    return getTranslation(`${this.platform.key}.externalLink.updateInfo`);
  }

  get phoneMask(): string {
    return this.form.phoneNumber && this.form.phoneNumber.startsWith('0') ? '##########' : '0#########';
  }

  async handleUpdateUserFields() {
    if ((this.$refs.updateProfileForm as Vue & { validate: () => boolean }).validate()) {
      try {
        const updatedUser = (await UserServices.updateUserFields(this.userId, this.form, 'fr')).data;

        if (updatedUser.email !== this.initialForm.email) {
          this.mustReconnect = true;
          this.showConfirm = true;
        } else {
          await Promise.all([UserServices.refreshUser(), UserServices.refreshUserPoints()]);
        }
      } catch (e) {
        if (e.isAxiosError && e.response.status === 500) {
          this.errorContactingService = 'Une erreur est survenu lors de le mise à jour du compte. Veuillez réessayer plus tard.';
        }
        if (e.isAxiosError && e.response.status === 400) {
          if (Array.isArray(e.response.data.message)) {
            e.response.data.message.forEach((error: any) => {
              if (error.property === 'dateOfBirth') {
                this.errorDateOfBirth = 'Date de naissance invalide';
              }
            });
          }
        }
      }
    }
    this.validIsDisabled = true;
  }

  onUserInfoChange() {
    this.validIsDisabled = true;
    Object.keys(this.initialForm).forEach((key) => {
      if (this.initialForm[key] !== this.form[key]) {
        this.validIsDisabled = false;
      }
    });
  }

  get isDisabled(): boolean {
    return this.validIsDisabled;
  }

  get isDisabledEmail(): boolean {
    let disabled = false;
    if (this.initialForm.email !== this.form.email) {
      disabled = true;
    }

    return disabled;
  }

  closeConfirm() {
    this.showConfirm = false;
  }

  get hasReadOnlyRole() {
    return this.$store.getters['user/isReadOnly'];
  }

  async sendMail() {
    this.emailResentLoading = true;
    const result = await UserServices.sendConfirmationEmail('fr');
    // @ts-ignore : ts just get one type of response but axios can send multiple type of response
    if (result.isAxiosError && result.response.status === 409) {
      this.emailResentError = 'Votre adresse email est déjà validée';
      // For email field disabling
      this.hasConfirmedEmail = true;
    } else {
      this.emailResent = true;
    }
    this.emailResentLoading = false;
    this.snackbar = true;
  }

  get isMinorAndHaveFunctionalityEnabled() {
    const isMinorModeration = this.$store.getters['platform/isMinorModerationActive'];
    if (!isMinorModeration) {
      return false;
    }
    const isMinor = this.$store.getters['user/isMinor'];
    return isMinor;
  }

  get displayEmailResent() {
    return !this.hasConfirmedEmail && !this.isMinorAndHaveFunctionalityEnabled;
  }
}
